/**
 * Confirm Dialog
 * Reusable confirmation dialog covering the shared Dialog -> Header -> Footer
 * (Cancel + pending action) shape used across the admin UI.
 * @packageDocumentation
 */

import type { ReactNode } from 'react';

import {
  Button,
  Dialog,
  DialogTitle,
  DialogFooter,
  DialogHeader,
  DialogContent,
  DialogDescription,
} from '@/components/ui';

/**
 * Props for the ConfirmDialog component.
 */
export interface ConfirmDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** The dialog title. */
  title: string;
  /** Whether the confirm action is pending (adds loading + disabled). Defaults to false. */
  pending?: boolean;
  /** Label for the cancel button. */
  cancelLabel: string;
  /** Label for the confirm button. */
  confirmLabel: string;
  /** Label shown on the confirm button while pending; defaults to confirmLabel. */
  pendingLabel?: string;
  /** Whether the confirm button uses the destructive variant. Defaults to false. */
  destructive?: boolean;
  /** Callback invoked when the user confirms. Never triggers the cancel/close path. */
  onConfirm: () => void;
  /** Callback invoked when the user cancels. Defaults to closing the dialog. */
  onCancel?: () => void;
  /** Optional description; callers may pass JSX (e.g. a font-mono span). */
  description?: ReactNode;
  /** Callback when the dialog open state changes (escape/overlay/close also fire this). */
  onOpenChange: (open: boolean) => void;
}

/**
 * A reusable confirmation dialog with a cancel button and a (optionally
 * destructive, optionally pending) confirm button.
 *
 * The confirm button calls `onConfirm` directly and never triggers the
 * cancel/close side-effects, so call sites whose cancel path has side-effects
 * (e.g. an unsaved-changes guard) stay correct.
 *
 * @param { ConfirmDialogProps } props Component props.
 * @returns { JSX.Element } The confirmation dialog element.
 */
export const ConfirmDialog = ({
  open,
  title,
  onCancel,
  onConfirm,
  description,
  cancelLabel,
  confirmLabel,
  pendingLabel,
  onOpenChange,
  pending = false,
  destructive = false,
}: ConfirmDialogProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        {description && <DialogDescription>{description}</DialogDescription>}
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel ?? (() => onOpenChange(false))}>
          {cancelLabel}
        </Button>
        <Button
          variant={destructive ? 'destructive' : 'default'}
          loading={pending}
          disabled={pending}
          onClick={onConfirm}
        >
          {pending ? (pendingLabel ?? confirmLabel) : confirmLabel}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
